import React from 'react';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import { parsePhoneInput } from '../../utils/validators';
import AuthCountrySelect from './AuthCountrySelect';
import {
  AUTH_MODAL_COLOR,
  AUTH_MODAL_COPY,
  authModalMetrics,
  authModalType,
  AuthModalLayout,
} from './authModal.constants';

export type AuthPhoneValue = {
  /** Up to 10 national digits, no dial code. */
  phone: string;
  /** ISO code, e.g. "IN". */
  countryCode: string;
};

type AuthPhoneFieldProps = AuthPhoneValue & {
  layout: AuthModalLayout;
  /** id of the number input, so the label can point at it. */
  id: string;
  onChange: (value: AuthPhoneValue) => void;
  label?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Show the picked number without editing, e.g. on a code screen. */
  readOnly?: boolean;
  /** Shown at the end of the number box, e.g. a Change link. */
  endAction?: React.ReactNode;
};

/**
 * Mobile number field of the login screens: label, country dial-code picker and the
 * number. Typing, paste and autofill ("+44 …") go through parsePhoneInput, so a dial
 * code also moves the picker. Which countries can get a code is checked on send.
 */
function AuthPhoneField({
  layout,
  id,
  phone,
  countryCode,
  onChange,
  label,
  disabled,
  autoFocus,
  readOnly,
  endAction,
}: AuthPhoneFieldProps) {
  const metrics = authModalMetrics(layout);
  const type = authModalType(layout);
  const text = label || AUTH_MODAL_COPY.mobileLabel;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${metrics.fieldGap}px` }}>
      <Box component="label" htmlFor={id} sx={{ ...type.label, color: AUTH_MODAL_COLOR.text }}>
        {text}
      </Box>
      <Box sx={{ display: 'flex', gap: `${metrics.inputGap}px` }}>
        <AuthCountrySelect
          layout={layout}
          value={countryCode}
          onChange={readOnly ? undefined : (next) => onChange({ phone, countryCode: next })}
          disabled={disabled}
        />
        <InputBase
          id={id}
          value={phone}
          onChange={(event) => onChange(parsePhoneInput(event.target.value, countryCode))}
          placeholder={AUTH_MODAL_COPY.mobilePlaceholder}
          autoFocus={autoFocus}
          readOnly={readOnly}
          endAdornment={endAction}
          inputProps={{
            inputMode: 'numeric',
            // The full number, so the dial code can set the country picker.
            autoComplete: 'tel',
            'aria-label': text,
          }}
          sx={{
            flex: 1,
            height: metrics.fieldHeight,
            px: `${metrics.dividerGap}px`,
            borderRadius: `${metrics.fieldGap}px`,
            backgroundColor: AUTH_MODAL_COLOR.field,
            color: AUTH_MODAL_COLOR.text,
            ...type.input,
            '& .MuiInputBase-input': {
              padding: 0,
              height: 'auto',
              ...type.input,
              color: AUTH_MODAL_COLOR.text,
              cursor: readOnly ? 'default' : 'text',
            },
            '& input::placeholder': {
              color: AUTH_MODAL_COLOR.muted,
              opacity: 1,
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default AuthPhoneField;
